import React from 'react';
import classNames from 'classnames';
import { Button, message } from '@myua/kit';
import { assetBaseName, MagicPrimaryAsset } from '../model';
import { captureCurrentFrame, CaptureMode } from '../capture';
import { CheckIcon, CloseIcon, MagicIcon } from '../icons';
import { useBasket } from '../useBasket';
import '../styles.scss';

// Entry points that stage an asset into the Magic Studio basket. There is no
// longer a direct "open the studio for this asset" shortcut — the basket (the
// floating tray, MagicBasketTray) is the single way into the studio, so every
// per-asset control is a toggle that adds / removes the asset from it.

// AssetDetails sidebar — the primary entry point. Before staging, a single
// full-width gradient "Add to Magic Studio" CTA. Once staged it splits (the
// e-commerce "add to cart → in cart" pattern) into two controls right where the
// user just clicked: "Added" (click to remove) and a prominent "Open Magic
// Studio" — so opening the studio is obvious without hunting for the floating
// tray below the fold.
export const SidebarBasketButton: React.FC<{ asset: MagicPrimaryAsset }> = ({ asset }) => {
  const { isInBasket, toggle, open } = useBasket();
  const added = isInBasket(asset.id);

  if (!added) {
    return (
      <Button
        block
        className="al-magic-sidebar-button"
        icon={<MagicIcon size={14} />}
        onClick={() => toggle(asset, 'sidebar')}
      >
        Add to Magic Studio
      </Button>
    );
  }

  return (
    <div className="al-magic-sidebar-split">
      <Button
        className="al-magic-sidebar-split__remove"
        title="Remove from Magic Studio"
        onClick={() => toggle(asset, 'sidebar')}
      >
        {/* Check by default, swapped to an × on hover (handled in CSS) to read
            as "added — click to remove". */}
        <span className="al-magic-sidebar-split__remove-check">
          <CheckIcon size={14} />
        </span>
        <span className="al-magic-sidebar-split__remove-x">
          <CloseIcon size={12} />
        </span>
        Added
      </Button>
      <Button
        className="al-magic-sidebar-split__open"
        icon={<MagicIcon size={14} />}
        onClick={() => open('sidebar')}
      >
        Open Magic Studio
      </Button>
    </div>
  );
};

// Fullscreen preview toolbar — the Magic Studio action, a gradient wand button
// that stands out from the plain viewer icons. For a static image it toggles the
// asset in the basket; for a video or 3D asset it captures the frame currently on
// screen and stages that as a reference.
export const FullscreenBasketButton: React.FC<{
  asset: MagicPrimaryAsset;
  captureMode?: CaptureMode | null;
}> = ({ asset, captureMode }) => {
  const { isInBasket, toggle, addCapture } = useBasket();
  const added = isInBasket(asset.id);

  if (captureMode) {
    const onCapture = () => {
      const frame = captureCurrentFrame(captureMode, assetBaseName(asset));
      if (frame) addCapture(frame.name, frame.url);
      else void message.warning("Couldn't capture this frame — try again once it's playing.");
    };
    return (
      <button
        type="button"
        className="al-magic-toolbar-btn"
        title="Capture frame → Magic Studio"
        onClick={onCapture}
      >
        <MagicIcon size={16} />
      </button>
    );
  }

  return (
    <button
      type="button"
      className={classNames('al-magic-toolbar-btn', { 'al-magic-toolbar-btn_added': added })}
      title={added ? 'Added — remove from Magic Studio' : 'Add to Magic Studio'}
      onClick={() => toggle(asset, 'fullscreen')}
    >
      {added ? <CheckIcon size={16} /> : <MagicIcon size={16} />}
    </button>
  );
};
